import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams } from 'react-router-dom';
import {
  fetchTicketsInfoAPI,
  IEarnings,
  ILedgerEntry,
  IRaqtInvoice,
  TicketStatusEnum,
} from '../../apis/tournament';
import { BackIcon } from '../../assets/icons/BackIcon';
import { ItemWrapper, RaqtTable, Switcher } from '../../components';
import { IRaqtTableConfig } from '../../components/table/RaqtTable';
import { selectTournamentData } from '../../features/tournament/tournamentSelectors';
import { useAppSelector } from '../../hooks/redux';
import { getDateWithTime } from '../../utils/getStringDate';

import './TournamentTicketsStats.scss';
import { ticketStatusTranslation } from '../../utils/ticket';

const prefix = 'tournamentStatsTickets-';

enum SubTiketPagesEnum {
  TICKETS,
  SUMMERY,
}

/** Currency formatting — "SEK 88 000.00": uppercase code prefix, space thousands
 *  separator, dot decimals. Uses non-breaking spaces so the amount never wraps across
 *  lines. Backend sends lowercase ISO codes (e.g. "sek"). */
const formatMoney = (n: number, currency: string | null) => {
  const grouped = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(n ?? 0)
    .replace(/,/g, '\u00A0');
  return `${(currency ?? 'sek').toUpperCase()}\u00A0${grouped}`;
};

const ledgerPlayerName = (e: ILedgerEntry) =>
  [e.firstName, e.lastName].filter(Boolean).join(' ') || e.purchasedTicket || '—';

/** Status badge colour only — a ticket line that represents money kept. */
const isMoneyStatus = (status?: TicketStatusEnum | null) =>
  status === TicketStatusEnum.PAID ||
  status === TicketStatusEnum.DISABLED ||
  status === TicketStatusEnum.TRANSFERRED;

export const TournamentTicketsStats = () => {
  const { t } = useTranslation();
  const [ledger, setLedger] = useState<ILedgerEntry[]>([]);
  const [earnings, setEarnings] = useState<IEarnings | null>(null);
  const currentTournamet = useAppSelector(selectTournamentData);
  const [page, setPage] = useState<SubTiketPagesEnum>(SubTiketPagesEnum.TICKETS);
  const [raqtInfo, setRaqtInfo] = useState({} as IRaqtInvoice);
  const { id, communityId } = useParams<{ id: string; communityId: string }>();

  const classList = [
    { label: t(prefix + 'allTickets'), value: SubTiketPagesEnum.TICKETS },
    { label: t(prefix + 'summary'), value: SubTiketPagesEnum.SUMMERY },
  ];

  // "To invoice RAQT" breakdown, fed by the backend `earnings` object (the source of truth).
  // Reconciles: classRevenue + registrationFeesTotal − raqtFee − refundsTotal === organizerNet.
  const renderInvoiceSummary = () => {
    if (!earnings) return null;
    const c = earnings.currency;
    const vatIncluded =
      earnings.vatTotal ?? (earnings.classVatTotal ?? 0) + (earnings.registrationFeesVatTotal ?? 0);
    // Service-fee rate lives at the summary level (always a single tournament %).
    const feeLabel = `${t(prefix + 'summaryFee')} (${earnings.serviceFeeRate}%)`;
    return (
      <div className="tournament-tickets-stats__summary-user-wrapper mb-4">
        <div className="tournament-tickets-stats__summary-title">
          {t(prefix + 'summaryToInvoiceTitle').toLocaleUpperCase()}
        </div>
        <div className="tournament-tickets-stats__summary-user">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'summaryTickets', { tournamentName: currentTournamet?.name || '' })}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            {formatMoney(earnings.classRevenue, c)}
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'registrationFee')}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            {formatMoney(earnings.registrationFeesTotal, c)}
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user tournament-tickets-stats__earnings-subtotal">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'earningsGross')}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            <strong>{formatMoney(earnings.gross, c)}</strong>
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'summaryVatIncluded')}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            {formatMoney(vatIncluded, c)}
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {feeLabel}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            <>
              <span className="font-weight-bold mr-2"> - </span>
              {formatMoney(earnings.raqtFee, c)}
            </>
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'summaryRefund')}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            <>
              <span className="font-weight-bold mr-2"> - </span>
              {formatMoney(earnings.refundsTotal, c)}
            </>
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__summary-user tournament-tickets-stats__earnings-net">
          <ItemWrapper className="tournament-tickets-stats__summary-user-left">
            {t(prefix + 'summaryToInvoice')}
          </ItemWrapper>
          <ItemWrapper className="tournament-tickets-stats__summary-user-right">
            <strong>{formatMoney(earnings.organizerNet, c)}</strong>
          </ItemWrapper>
        </div>
        <div className="tournament-tickets-stats__earnings-caveat">
          {t(prefix + 'earningsCaveat')}
        </div>
      </div>
    );
  };

  // Localized label for a ledger row (class name, or fee / refund line).
  const ledgerLabel = (e: ILedgerEntry) => {
    if (e.type === 'registration_fee') return t(prefix + 'registrationFee');
    if (e.type === 'refund') {
      const refund = t(prefix + 'refunded');
      return e.class ? `${refund} — ${e.class}` : refund;
    }
    return e.class ?? t(prefix + 'class');
  };

  // Localized status text. Transferred tickets name the new holder, whose ticket carries
  // paymentId `TRANSFERRED_FROM_<originalTicketId>`.
  const ledgerStatus = (e: ILedgerEntry) => {
    if (e.status == null) return '';
    if (e.status === TicketStatusEnum.TRANSFERRED) {
      const target = ledger.find((o) => o.paymentId === `TRANSFERRED_FROM_${e.ticketId}`);
      return target
        ? t(prefix + 'status-transferred', { playerName: ledgerPlayerName(target) })
        : t(prefix + 'status-transferredPlain');
    }
    return t(ticketStatusTranslation(e.status));
  };

  // Display rows for the table — render the ledger row-for-row, no money math.
  const ledgerRows = ledger.map((e) => ({
    class: ledgerLabel(e),
    playerName: ledgerPlayerName(e),
    amount: formatMoney(e.amount, e.currency),
    vat: formatMoney(e.vat, e.currency),
    status: ledgerStatus(e),
    date: e.date ? getDateWithTime(e.date) : '',
    paymentId: e.paymentId ?? '',
    type: e.type,
    rawStatus: e.status,
  }));

  // `isStale` guards against out-of-order responses: if the route id changes mid-request,
  // a slower response for the previous tournament must not overwrite the newer one.
  const getTickets = async (isStale: () => boolean) => {
    // Clear up front so a failed refetch (e.g. after an id change) can't show stale data.
    setLedger([]);
    setEarnings(null);
    setRaqtInfo({} as IRaqtInvoice);
    try {
      const { ledger, earnings, raqtDetails } = await fetchTicketsInfoAPI(+id);
      if (isStale()) return;
      setLedger(ledger ?? []);
      setEarnings(earnings ?? null);
      setRaqtInfo(raqtDetails ?? ({} as IRaqtInvoice));
    } catch (e) {
      // state already cleared above
    }
  };

  const moneyCellClass = (row: { type?: string }) =>
    row.type === 'refund' ? 'price--refund' : '';

  const tableConfig: IRaqtTableConfig = {
    blankHead: true,
    columns: [
      {
        label: t(prefix + 'class'),
        key: 'class',
        position: 'left',
        columnSize: 'big',
      },
      {
        label: t(prefix + 'playerName'),
        key: 'playerName',
        position: 'left',
        columnSize: 'big',
      },
      {
        label: t(prefix + 'amount'),
        key: 'amount',
        position: 'right',
        columnSize: 'small',
        render: (row) => (
          <ItemWrapper white={true}>
            <span className={moneyCellClass(row)}>{row.amount}</span>
          </ItemWrapper>
        ),
      },
      {
        label: t(prefix + 'vat'),
        key: 'vat',
        position: 'right',
        columnSize: 'small',
        render: (row) => (
          <ItemWrapper white={true}>
            <span className={moneyCellClass(row)}>{row.vat}</span>
          </ItemWrapper>
        ),
      },
      {
        label: t(prefix + 'status'),
        key: 'status',
        position: 'left',
        render: (row) => {
          if (!row.status) return <ItemWrapper white={true}>—</ItemWrapper>;
          const badgeClass = isMoneyStatus(row.rawStatus) ? 'badge-included' : 'badge-excluded';
          return (
            <ItemWrapper white={true}>
              <span className={`status-badge ${badgeClass}`}>{row.status}</span>
            </ItemWrapper>
          );
        },
      },
      {
        label: t(prefix + 'date'),
        key: 'date',
        position: 'left',
      },
      {
        label: t(prefix + 'paymentId'),
        key: 'paymentId',
        position: 'left',
        columnSize: 'big',
      },
    ],
  };

  const downloadTicketsCsv = () => {
    if (!ledger.length) return;

    const headers = [
      t(prefix + 'class'),
      t(prefix + 'playerName'),
      t(prefix + 'amount'),
      t(prefix + 'vat'),
      t(prefix + 'status'),
      t(prefix + 'date'),
      t(prefix + 'paymentId'),
    ];

    const escapeCsvValue = (value: unknown) => {
      let v = String(value ?? '');
      // Neutralize spreadsheet formula injection (user-controlled names etc).
      if (/^[=+\-@\t\r]/.test(v)) {
        v = `'${v}`;
      }
      if (v.includes('"') || v.includes(',') || v.includes('\n') || v.includes('\r')) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return v;
    };

    const rows = ledgerRows.map((row) => [
      row.class,
      row.playerName,
      row.amount,
      row.vat,
      row.status,
      row.date,
      row.paymentId,
    ]);

    // Trailing earnings summary so the CSV is self-reconciling.
    const summaryRows = earnings
      ? [
          [],
          [t(prefix + 'earningsGross'), '', formatMoney(earnings.gross, earnings.currency)],
          [
            t(prefix + 'earningsRaqtFee'),
            '',
            `-${formatMoney(earnings.raqtFee, earnings.currency)}`,
          ],
          [
            t(prefix + 'earningsRefunds'),
            '',
            `-${formatMoney(earnings.refundsTotal, earnings.currency)}`,
          ],
          [
            t(prefix + 'earningsOrganizerNet'),
            '',
            formatMoney(earnings.organizerNet, earnings.currency),
          ],
        ]
      : [];

    const csvContent = [headers, ...rows, ...summaryRows]
      .map((row) => row.map(escapeCsvValue).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const tournamentName = currentTournamet?.name || 'tournament';
    const safeName = tournamentName.replace(/[^a-z0-9]/gi, '_').toLowerCase();

    const link = document.createElement('a');
    link.href = url;
    link.download = `tickets_${safeName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    let stale = false;
    getTickets(() => stale);
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="tournament-tickets-stats">
      <div className="tournament-tickets-stats__title">{t(prefix + 'title')}</div>
      <div className="tournament-tickets-stats__back-link">
        <NavLink to={`/community/${communityId}/tournament/${id}/tags/dashboard`}>
          <BackIcon w="10" h="16" f="#0474EF" />{' '}
          <span className="tournament-tickets-stats__back-text">
            {t('tournamentDashboard-backButton')}
          </span>
        </NavLink>
      </div>
      <Switcher switches={classList} onSelect={setPage} selectedValue={page} />
      {page === SubTiketPagesEnum.TICKETS && (
        <div className="tournament-tickets-stats__table-holder">
          <div className="tournament-tickets-stats__actions">
            <button
              type="button"
              className="tournament-tickets-stats__export-button"
              onClick={downloadTicketsCsv}
              disabled={!ledger.length}
            >
              {t(prefix + 'exportCsv')}
            </button>
          </div>
          <RaqtTable config={tableConfig} data={ledgerRows} />
        </div>
      )}
      {page === SubTiketPagesEnum.SUMMERY && (
        <div className="tournament-tickets-stats__table-holder">
          <div className="tournament-tickets-stats__summary-title">
            {t(prefix + 'summaryRaqtDetailsTitle').toLocaleUpperCase()}
          </div>
          <div className="tournament-tickets-stats__summary-raqt-wrapper">
            <div className="tournament-tickets-stats__summary-raqt">
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-left">
                {t(prefix + 'summaryAddress')}
              </ItemWrapper>
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-right">
                {raqtInfo.invoiceAddress || ''}
              </ItemWrapper>
            </div>
            <div className="tournament-tickets-stats__summary-raqt">
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-left">
                {t(prefix + 'summaryVat')}
              </ItemWrapper>
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-right">
                {raqtInfo.raqtVAT || ''}
              </ItemWrapper>
            </div>
            <div className="tournament-tickets-stats__summary-raqt">
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-left">
                {t(prefix + 'summaryNumber')}
              </ItemWrapper>
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-right">
                {raqtInfo.organizationNumber || ''}
              </ItemWrapper>
            </div>
            <div className="tournament-tickets-stats__summary-raqt">
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-left">
                {t(prefix + 'summaryEmail')}
              </ItemWrapper>
              <ItemWrapper className="tournament-tickets-stats__summary-raqt-right">
                {raqtInfo.invoiceEmail || ''}
              </ItemWrapper>
            </div>
          </div>
          {renderInvoiceSummary()}
        </div>
      )}
    </div>
  );
};
